import {
  ArchiveBox,
  ArrowUturnLeft,
  DocumentText,
  PaperClip,
  PencilSquare,
  SquareTwoStack,
  Trash,
  XCircle,
} from '@medusajs/icons';
import { toast } from '@pcrm/fe/ui-kit';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { ActionMenuAction, ActionMenuGroup } from '../../components/action-menu';
import { ApiError } from '../../lib/api';
import { discardSigning } from '../signing/signing.api';
import { useCopySigningLink } from '../signing/use-copy-signing-link';
import { subscriptionsQueryKey } from '../subscriptions/subscriptions.api';
import type { DocumentOrigin } from './document-origin';
import {
  canArchive,
  canCancel,
  canDelete,
  canOpenEditor,
  canTerminate,
  DOCUMENT_SOURCE,
  DOCUMENT_STATE,
  DOCUMENTS_QUERY_KEY,
  type DocumentListItem,
  setDocumentArchived,
} from './documents.api';
import { openDocumentFile } from './open-document';

/**
 * Acțiunile unui document, **după starea lui**.
 *
 * Un singur constructor pentru amândouă listele — tabul „Documente" de pe fișa clientului
 * și registrul „Documente emise" — pentru că altfel operatorul ar avea două ecrane cu
 * reguli diferite pentru același act ([#115](https://github.com/bplugaru/efx-pcrm/issues/115)).
 * Semnarea n-are ecran propriu și nici tab: aici e tot ce se poate face cu un document.
 *
 * | actul | ce se poate face |
 * |---|---|
 * | `ciornă` | Trimite la semnat · Vezi PDF · Editează · **Șterge ciorna** |
 * | `trimis` | Copiază linkul · Vezi PDF · Invalidează linkul |
 * | primit, nesemnat | Vezi document · Anulează |
 * | `semnat` | Vezi document · **Reziliază** |
 * | `reziliat` | Vezi document · **Arhivează** / **Dezarhivează** |
 * | `anulat` | Vezi PDF · Arhivează / Dezarhivează |
 *
 * ⚠️ **Trei porți disjuncte în același loc din meniu**, și fiecare se numește după ce face:
 * „Șterge ciorna" pe un act neemis ([#142](https://github.com/bplugaru/efx-pcrm/issues/142)),
 * „Anulează" pe unul emis nesemnat, „Reziliază" pe unul semnat
 * ([#140](https://github.com/bplugaru/efx-pcrm/issues/140)). Meniul arată mereu exact una —
 * niciodată două, niciodată una stinsă.
 *
 * ⚠️ **„Șterge" și „Anulează" nu sunt același buton pe două acte.** Ce are număr a fost emis
 * și se anulează, cu motiv, rămânând pe vecie în registru; ce n-are număr e o ciornă și se
 * aruncă. Ce ajunge la anulare din meniul ăsta sunt actele **primite** nesemnate — iau număr
 * la încărcare — și cele **trimise la semnat** care nu se mai semnează: iau număr la trimitere
 * ([#221](https://github.com/bplugaru/efx-pcrm/issues/221)) și îl păstrează când linkul moare.
 *
 * ⚠️ **„Șterge ciorna" nu apare pe un act `trimis`.** De la #221 are număr, deci nici nu mai e
 * ciornă; pe cele plecate fără număr, înainte de #221, cât linkul e viu
 * serverul refuză scrierea (`409`): se invalidează linkul întâi, iar atunci actul se întoarce
 * în `ciornă` și butonul apare. Un buton care promite ce serverul refuză e o apăsare pierdută
 * de fiecare dată.
 *
 * ⚠️ **Un act reziliat nu se editează**, dar **se arhivează** (fondatorul, 2026-09-18: *„și
 * contractele reziliate pot fi arhivate"*). Fișierul lui semnat rămâne descărcabil din arhivă,
 * ca orice act arhivat: arhivarea ascunde rândul din listele zilnice, nu atinge fișierul.
 *
 * ⚠️ **Arhivarea apare pe actele ieșite din circulație** — anulat sau reziliat
 * ([#141](https://github.com/bplugaru/efx-pcrm/issues/141),
 * [#140](https://github.com/bplugaru/efx-pcrm/issues/140)). Pe unul **semnat** n-are ce căuta:
 * e în vigoare. Butonul e **același** pentru dus și întors, ca pe fișa clientului: se citește
 * `archivedAt`, nu starea.
 *
 * ⚠️ **„Editează" lipsește pe un act primit**, oricare ar fi starea: un PDF venit pe email
 * n-a fost niciodată text, deci editorul n-are ce deschide. Vezi `canOpenEditor`.
 *
 * ⚠️ **Se citesc două valori: `documentState` și `documentNumber`** (#142). Numărul e ce
 * deosebește ciorna de actul primit nesemnat — amândouă sunt `1` — deci `canDelete` și
 * `canCancel` îl cer. Restul regulilor citesc doar starea.
 *
 * ⚠️ **Al treilea nu se adaugă.** Într-o variantă intermediară rândul
 * purta și id-ul sesiunii de semnare, iar meniul citea perechea stare+sesiune — pentru că
 * `trimis` mințea: expirarea marca sesiunea și lăsa documentul acolo pe vecie. De când
 * expirarea întoarce documentul în `ciornă`, ca invalidarea, starea e din nou adevărul
 * întreg. Cine readuce o stare care nu se schimbă când linkul moare readuce și perechea.
 *
 * ⚠️ **„Vezi PDF" pe un act semnat merge pe altă rută**, care întoarce **fișierul salvat la
 * semnare**. `GET /documents/:id/pdf` randează versiunea înghețată, care nu conține
 * semnătura — ar tipări contractul semnat ca nesemnat, defectul reparat pe 2026-09-01.
 *
 * ⚠️ **Excepția: un act primit, în starea `semnat`.** El n-a trecut prin sesiunea noastră de
 * semnare, deci n-are fișier acolo; `GET /documents/:id/pdf` e chiar drumul care întoarce
 * ce a venit pe email. Ruta de semnare i-ar fi răspuns „nu există".
 */
export function useDocumentActions({
  onCancel,
  onDelete,
  onSend,
  extra,
  origin,
}: {
  /**
   * Anularea și **rezilierea** cer motiv, deci deschid sertarul ecranului care a cerut
   * acțiunile — **același sertar**, care se numește după starea actului (#140). De aceea
   * numele a rămas `onCancel`: e un singur drum, nu două.
   */
  onCancel: (document: DocumentListItem) => void;
  /**
   * Ștergerea ciornei cere o confirmare, nu un motiv (#142): ecranul deschide `Prompt`-ul lui.
   * Vezi `useDeleteDocumentPrompt`.
   */
  onDelete: (document: DocumentListItem) => void;
  /** Trimiterea la semnat deschide sertarul de livrare (email sau „doar link"). */
  onSend: (document: DocumentListItem) => void;
  /**
   * Ce mai adaugă ecranul care cere acțiunile — „Vezi clientul", din registrul global.
   *
   * Intră în **primul** grup, nu într-unul propriu la coadă: ce scoate actul din circulație
   * rămâne ultimul, despărțit de linie. Un „Vezi clientul" sub „Anulează" e o apăsare
   * greșită care așteaptă să se întâmple.
   */
  extra?: (document: DocumentListItem) => readonly ActionMenuAction[];
  /**
   * De unde se deschide documentul la „Editează" — fișa clientului o dă, ca „Înapoi" din
   * editor să ducă acolo (#222). Registrul nu o dă: întoarcerea implicită e la el.
   */
  origin?: DocumentOrigin;
}): (document: DocumentListItem) => ActionMenuGroup[] {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const copyLink = useCopySigningLink();

  const invalidateLink = useMutation({
    mutationFn: ({ documentId }: { documentId: string; customerId: string | null }) =>
      discardSigning(documentId),
    onSuccess: async (_result, { customerId }) => {
      toast.success('Linkul a fost invalidat. Documentul s-a întors în „ciornă".');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY }),
        // Abonamentele acoperite s-au deblocat odată cu documentul: ecranul lor arăta
        // butoane stinse care acum merg.
        // ⚠️ Doar pe un act de client. Un act de partener n-are abonamente și n-ajunge
        // niciodată la semnat — dar `customerId` e `NULL`-abilă de la #133, iar o cheie
        // construită pe `null` ar fi invalidat lista abonamentelor „clientului null".
        ...(customerId === null
          ? []
          : [queryClient.invalidateQueries({ queryKey: subscriptionsQueryKey(customerId) })]),
      ]);
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'Linkul nu a putut fi invalidat.');
    },
  });

  const archive = useMutation({
    mutationFn: ({ documentId, archived }: { documentId: string; archived: boolean }) =>
      setDocumentArchived(documentId, archived),
    onSuccess: async (_document, { archived }) => {
      toast.success(archived ? 'Document arhivat' : 'Document dezarhivat', {
        // Ce s-a întâmplat cu rândul, nu doar că s-a întâmplat: arhivat, el **dispare** din
        // lista deschisă, iar fără o vorbă despre unde s-a dus prima reacție e „l-am șters".
        description: archived
          ? 'A ieșit din liste. Îl găsești cu filtrul „doar arhivate".'
          : undefined,
      });
      await queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'Documentul nu a putut fi arhivat.');
    },
  });

  function openPdf(promise: Promise<void>): void {
    void promise.catch((error: unknown) =>
      // Mesajul serverului spune și ce e de făcut: un act emis înainte de generarea din
      // template n-are text în spate.
      toast.error(error instanceof ApiError ? error.message : 'PDF-ul nu a putut fi generat.'),
    );
  }

  return (document: DocumentListItem): ActionMenuGroup[] => {
    const state = document.documentState;
    // Doar actele **generate** au fișier salvat la semnare; unul primit se citește de pe
    // ruta obișnuită, care îi întoarce fișierul venit pe email.
    // „Vezi document", nu „Vezi PDF": la amândouă stările ce se deschide e **fișierul
    // semnat**, nu o randare. Un act reziliat a fost semnat — e singurul drum către starea
    // aia — deci fișierul e tot acolo, iar `openDocumentFile` îl cere de pe ruta de semnare.
    const signed =
      (state === DOCUMENT_STATE.signed || state === DOCUMENT_STATE.terminated) &&
      document.source === DOCUMENT_SOURCE.generated;
    const atSigning = state === DOCUMENT_STATE.sent;

    return [
      {
        actions: [
          // `trimis` nu apare aici: cât linkul e viu, al doilea ar fi refuzat cu `409`; iar
          // când moare, documentul se întoarce singur în `1`, cu numărul păstrat (#221) —
          // retrimiterea pleacă de aici, peste același act.
          //
          // ⚠️ **Nici pe un act de partener** (#139): semnarea se sprijină pe fișa clientului
          // — adresa de trimitere, arhivarea, dovada — iar serverul refuză cu
          // `document_not_customer_owned`. Până acum, în registru, un act de partener în
          // starea `generat` arăta butonul și eșua la apăsare. Ascuns, ca „Vezi clientul", nu
          // stins: un rând gri într-un meniu cere o explicație pe care n-avem unde s-o dăm.
          ...(state === DOCUMENT_STATE.draft && document.customerId !== null
            ? [{ icon: <PaperClip />, label: 'Trimite la semnat', onClick: () => onSend(document) }]
            : []),
          ...(atSigning
            ? [
                {
                  icon: <SquareTwoStack />,
                  label: 'Copiază linkul',
                  onClick: () => copyLink.mutate(document.id),
                },
              ]
            : []),
          {
            icon: <DocumentText />,
            label: signed ? 'Vezi document' : 'Vezi PDF',
            // Care rută întoarce conținutul e o singură decizie, scrisă în `openDocumentFile`:
            // clicul pe rând o cere și el, iar două copii ar diverge la prima stare nouă.
            onClick: () => openPdf(openDocumentFile(document)),
          },
          // **Editarea, nu anularea, e răspunsul la o greșeală de o literă.** Apare doar
          // până la semnare: după, actul se desface cu alt act. Serverul refuză scrierea cât
          // linkul e viu (`409`), deci nici butonul n-are ce promite pe un act `trimis`.
          // Vezi docs/wiki/domains/document-manager.md#editarea-până-la-semnare
          ...(!atSigning && canOpenEditor(document)
            ? [
                {
                  icon: <PencilSquare />,
                  label: 'Editează',
                  onClick: () =>
                    void navigate({
                      to: '/documente/$documentId',
                      params: { documentId: document.id },
                      search: origin ?? {},
                    }),
                },
              ]
            : []),
          ...(extra === undefined ? [] : extra(document)),
        ],
      },
      {
        // Ce scoate actul din circulație stă în grupul de jos, despărțit de linie:
        // invalidarea linkului pe unul trimis, anularea pe restul.
        actions: atSigning
          ? [
              {
                icon: <XCircle />,
                label: 'Invalidează linkul',
                onClick: () =>
                  invalidateLink.mutate({
                    documentId: document.id,
                    customerId: document.customerId,
                  }),
              },
            ]
          : // **Ciorna se șterge, actul emis se anulează** (#142). Prima ramură după semnare, ca
            // ordinea din meniu să nu depindă de care dintre ele e mai frecventă.
            canDelete(document)
            ? [{ icon: <Trash />, label: 'Șterge ciorna', onClick: () => onDelete(document) }]
            : canCancel(document)
              ? [{ icon: <XCircle />, label: 'Anulează', onClick: () => onCancel(document) }]
              : // ⚠️ **Rezilierea trece înaintea arhivării**, deși un act semnat n-ar putea fi
                // arhivat oricum: ordinea `if`-urilor e ce ține meniul citibil când regulile se
                // mai schimbă o dată.
                canTerminate(state)
                ? // Același sertar ca anularea, alt cuvânt: actul a fost bun și s-a terminat.
                  // ⚠️ **Fără `document.customerId !== null`**, spre deosebire de „Trimite la
                  // semnat": un act de partener n-ajunge niciodată la `semnat`, deci
                  // `canTerminate` a exclus deja cazul. O verificare în plus aici ar fi sugerat
                  // că există acte de partener semnate.
                  [{ icon: <XCircle />, label: 'Reziliază', onClick: () => onCancel(document) }]
                : // Un act ieșit din circulație: singurul lucru care-i mai rămâne e să iasă din
                  // liste.
                  // ⚠️ **Se citește `archivedAt`, nu starea** — un act arhivat e tot `anulat`
                  // sau `reziliat`, deci starea n-ar fi putut spune în ce sens merge butonul.
                  canArchive(state) || document.archivedAt !== null
                  ? [
                      {
                        icon: document.archivedAt === null ? <ArchiveBox /> : <ArrowUturnLeft />,
                        label: document.archivedAt === null ? 'Arhivează' : 'Dezarhivează',
                        onClick: () =>
                          archive.mutate({
                            documentId: document.id,
                            archived: document.archivedAt === null,
                          }),
                      },
                    ]
                  : [],
      },
    ];
  };
}
